import { useEffect, useState } from 'react'

import { VERSION } from '@core/release'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  ESTATISTICAS_VAZIAS,
  type RendererStats,
  calcularFps,
  formatarNumero,
  lerEstatisticas,
} from '@core/layout/statusbar/rendererStats'

/** Barra de estado: o que está carregado e quanto custa a desenhar.
 *
 *  Até aqui estes números ou não existiam ou estavam escondidos no
 *  painel de diagnóstico, que ninguém abre a meio do trabalho. São
 *  precisamente os números que explicam a pergunta "porque é que isto
 *  está lento", e por isso têm de estar sempre à vista.
 *
 *  Custo: uma leitura de meio em meio segundo a `renderer.info`, que o
 *  three já mantém actualizado. Não se entra no ciclo de desenho. */
export default function StatusBar() {
  const models = useAppStore((s) => s.models)
  const selecionados = useAppStore((s) => s.selectedTargets.length)
  const [stats, setStats] = useState<RendererStats>(ESTATISTICAS_VAZIAS)

  useEffect(() => {
    let frameAnterior = 0
    let instanteAnterior = performance.now()
    const ler = () => {
      const world = viewerApi.getWorld()
      const renderer = (
        world as unknown as {
          renderer?: {
            three?: {
              info?: { render?: { triangles?: number; calls?: number; frame?: number } }
              getContext?: () => unknown
            }
          }
        } | null
      )?.renderer?.three
      if (!renderer) {
        setStats(ESTATISTICAS_VAZIAS)
        return
      }
      const agora = performance.now()
      const frame = renderer.info?.render?.frame ?? 0
      const fps = calcularFps(frameAnterior, frame, agora - instanteAnterior)
      frameAnterior = frame
      instanteAnterior = agora
      let contexto: unknown = null
      try {
        contexto = renderer.getContext?.() ?? null
      } catch {
        // Um contexto perdido atira aqui. Fica sem motor, e mais nada.
      }
      setStats(lerEstatisticas(renderer.info, contexto, fps))
    }
    const id = setInterval(ler, 500)
    return () => clearInterval(id)
  }, [])

  const totalElementos = models.reduce(
    (soma, m) => soma + (m.entityCount ?? 0),
    0,
  )

  return (
    <div
      data-tour="barra-estado"
      className="flex items-center gap-4 border-t border-[var(--color-border)] bg-[var(--color-panel)] px-3 py-1 text-[11px] text-[var(--color-text-dim)] select-none"
    >
      <Campo
        valor={`${models.length} ${models.length === 1 ? 'modelo' : 'modelos'}`}
        titulo="Modelos carregados na cena"
      />
      {totalElementos > 0 && (
        <Campo
          valor={`${formatarNumero(totalElementos)} entidades`}
          titulo="Total de entidades dos modelos carregados"
        />
      )}
      {selecionados > 0 && (
        <Campo
          valor={`${selecionados} ${selecionados === 1 ? 'selecionado' : 'selecionados'}`}
          titulo="Elementos na seleção atual"
        />
      )}
      <span className="flex-1" />
      {stats.triangulos > 0 && (
        <Campo
          valor={`${formatarNumero(stats.triangulos)} tri`}
          titulo="Triângulos desenhados na última imagem"
        />
      )}
      {stats.chamadas > 0 && (
        <Campo
          valor={`${formatarNumero(stats.chamadas)} draws`}
          titulo="Chamadas de desenho na última imagem. Quantas menos, melhor."
        />
      )}
      {stats.fps !== null && (
        <Campo
          valor={`${stats.fps} fps`}
          titulo="Imagens por segundo, medidas de meio em meio segundo"
        />
      )}
      {stats.memoriaMB !== null && (
        <Campo
          valor={`${formatarNumero(stats.memoriaMB)} MB`}
          titulo="Memória do JavaScript em uso (só o Chromium a reporta)"
        />
      )}
      {stats.motor && <Campo valor={stats.motor} titulo="Motor de desenho" />}
      <Campo valor={VERSION} titulo="Versão desta aplicação" />
    </div>
  )
}

function Campo({ valor, titulo }: { valor: string; titulo: string }) {
  return (
    <span title={titulo} className="whitespace-nowrap tabular-nums">
      {valor}
    </span>
  )
}
